import ApiService from "./ApiService";
import { normalizeMatch } from "./normalizers";

class MatchController {
  constructor() {
    this.apiService = new ApiService();
  }

  /**
   * Get a match by ID
   * @param {string} matchId - The UUID of the match
   * @returns {Promise<Object>} - The match object
   */
  async getMatchById(matchId) {
    const match = await this.apiService.get(`app/match/${matchId}`);
    return normalizeMatch(match);
  }

  /**
   * Add a goal to a match
   * @param {string} matchId - The UUID of the match
   * @param {Object} goalData - The details of the goal
   * @returns {Promise<Object>} - Response status
   */
  async addGoal(matchId, goalData) {
    return this.apiService.post(`app/match/${matchId}/goal`, goalData);
  }

  /**
   * Add a red card to a player in a match
   * @param {string} matchId - The UUID of the match
   * @param {Object} cardData - The details of the red card
   * @returns {Promise<Object>} - Response status
   */
  async addRedCard(matchId, cardData) {
    return this.apiService.post(`app/match/${matchId}/redCard`, cardData);
  }

  /**
   * Add a yellow card to a player in a match
   * @param {string} matchId - The UUID of the match
   * @param {Object} cardData - The details of the yellow card
   * @returns {Promise<Object>} - Response status
   */
  async addYellowCard(matchId, cardData) {
    return this.apiService.post(`app/match/${matchId}/yellowCard`, cardData);
  }

  /**
   * Add a yellow-red card to a player in a match
   * @param {string} matchId - The UUID of the match
   * @param {Object} cardData - The details of the yellow-red card
   * @returns {Promise<Object>} - Response status
   */
  async addYellowRedCard(matchId, cardData) {
    return this.apiService.post(`app/match/${matchId}/yellowRedCard`, cardData);
  }

  /**
   * Add a player to the home blanket
   * @param {string} matchId - The UUID of the match
   * @param {string} playerId - The UUID of the player to be added
   * @param {number} playerNumber - The overridden player number
   * @param {Object} [coach] - The coach details, if available
   * @returns {Promise<Object>} - Response status
   */
  async addPlayerToHomeBlanket(matchId, playerId, playerNumber, coach) {
    const playerData = {
      playerId,
      number: playerNumber,
      coach, // Coach will be added only if provided
    };
    return this.apiService.post(
      `app/match/${matchId}/homeBlankett/addPlayer`,
      playerData
    );
  }

  /**
   * Add a player to the away blanket
   * @param {string} matchId - The UUID of the match
   * @param {string} playerId - The UUID of the player to be added
   * @param {number} playerNumber - The overridden player number
   * @param {Object} [coach] - The coach details, if available
   * @returns {Promise<Object>} - Response status
   */
  async addPlayerToAwayBlanket(matchId, playerId, playerNumber, coach) {
    const playerData = {
      playerId,
      number: playerNumber,
      coach, // Coach will be added only if provided
    };
    return this.apiService.post(
      `app/match/${matchId}/awayBlankett/addPlayer`,
      playerData
    );
  }

  /**
   * Remove a player from the home blanket
   * @param {string} matchId - The UUID of the match
   * @param {string} playerId - The UUID of the player to be removed
   * @returns {Promise<Object>} - Response status
   */
  async removePlayerFromHomeBlanket(matchId, playerId) {
    return this.apiService.delete(
      `app/match/${matchId}/${playerId}/homeBlankett/removePlayer`
    );
  }

  /**
   * Remove a player from the away blanket
   * @param {string} matchId - The UUID of the match
   * @param {string} playerId - The UUID of the player to be removed
   * @returns {Promise<Object>} - Response status
   */
  async removePlayerFromAwayBlanket(matchId, playerId) {
    return this.apiService.delete(
      `app/match/${matchId}/${playerId}/awayBlankett/removePlayer`
    );
  }

  /**
   * Start a game
   * @param {string} matchId - The UUID of the match
   * @returns {Promise<Object>} - Response status
   */
  async startGame(matchId) {
    return this.apiService.patch(`app/match/${matchId}/startGame`);
  }

  /**
   * End the first half of a game
   * @param {string} matchId - The UUID of the match
   * @returns {Promise<Object>} - Response status
   */
  async endFirstHalf(matchId) {
    return this.apiService.patch(`app/match/${matchId}/endFirstHalf`);
  }

  /**
   * Start the second half of a game
   * @param {string} matchId - The UUID of the match
   * @returns {Promise<Object>} - Response status
   */
  async startSecondHalf(matchId) {
    return this.apiService.patch(`app/match/${matchId}/startSecondHalf`);
  }

  /**
   * End a game
   * @param {string} matchId - The UUID of the match
   * @returns {Promise<Object>} - Response status
   */
  async endGame(matchId) {
    return this.apiService.patch(`app/match/${matchId}/endGame`);
  }

  /**
   * Submit a game report
   * @param {string} matchId - The UUID of the match
   * @param {string} reportText - The match report text
   * @returns {Promise<Object>} - Response status
   */
  async submitGame(matchId, reportText) {
    const data = { text: reportText };
    return this.apiService.patch(`app/match/${matchId}/submit`, data);
  }

  /**
   * Mark a game as a no-show
   * @param {string} matchId - The UUID of the match
   * @param {string} winningTeam - The team that won due to no-show ("home" or "away")
   * @returns {Promise<Object>} - Response status
   */
  async noShowGame(matchId, winningTeam) {
    const data = { winningTeam };
    return this.apiService.patch(`app/match/${matchId}/noShowGame`, data);
  }

  /**
   * Mark a game as a Team Cancel
   * @param {string} matchId - The UUID of the match
   * @param {string} winningTeam - The team that won due to no-show ("home" or "away")
   * @returns {Promise<Object>} - Response status
   */
  async teamCancel(matchId, winningTeam) {
    const data = { winningTeam };
    return this.apiService.patch(`app/match/${matchId}/teamcancel`, data);
  }

  /**
   * Mark a game as abandoned (spielabbruch)
   * @param {string} matchId - The UUID of the match
   * @returns {Promise<Object>} - Response status
   */
  async spielAbbruch(matchId) {
    return this.apiService.patch(`app/match/${matchId}/spielabbruch`);
  }

  /**
   * Complete the match and update team points based on the result
   * @param {string} matchId - The UUID of the match
   * @returns {Promise<Object>} - Response status
   */
  async completeMatch(matchId) {
    return this.apiService.patch(`app/match/${matchId}/done`);
  }

  /**
   * Toggle the dress for a team's blanket in a match
   * @param {string} matchId - The UUID of the match
   * @param {string} team - The team to toggle dress for ("home" or "away")
   * @returns {Promise<Object>} Response status
   */
  async toggleDress(matchId, team) {
    const toggleData = { team };
    return this.apiService.post(`app/match/${matchId}/toggle`, toggleData);
  }

  /**
   * Extends the payment term for a team.
   * @param {string} teamID - The ID of the team for which the overdraft is requested.
   * @returns {Promise} A promise resolving to the HTTP response or an error.
   */
  async overdraft(teamID) {
    return this.apiService.get(`app/team/${teamID}/overdraft`);
  }
}

export default MatchController;
